import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { captureEvent } from "@/lib/analytics";
import { ArrowRight, Github, Gitlab, Menu, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { SESSION_CONTEXT, SUBSCRIBE_TO_UPDATES } from "@/graphql/queries";
import { normalizeCampaign, saveAuthCampaign } from "@/lib/authRedirect";
import { cn } from "@/lib/utils";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useProviderSignIn } from "@/hooks/useProviderSignIn";
import type { ProviderId } from "@/lib/providerRegistry";
import { HomepageDemoPreview } from "./HomepageDemoPreview";
import { HomepageHaystack } from "./HomepageHaystack";
import { marketingThemeStyle } from "./marketing/marketingTheme";
import { SiteFooter } from "./site/SiteFooter";

/**
 * `/` - Landing page for unauthenticated visitors.
 * Links to `/app` for the main app (which redirects to `/login` if needed).
 */
export default function HomePage() {
  useDocumentTitle("DeployCompare · Review Terraform deploy risk");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const {
    data,
    loading: sessionLoading,
    error: sessionError,
  } = useQuery<{
    sessionContext: { authenticated: boolean };
  }>(SESSION_CONTEXT, {
    context: { operationKind: "account" }, // account-scoped: session cookie only
    fetchPolicy: "cache-and-network",
    errorPolicy: "ignore",
  });
  const isAuthenticated = data?.sessionContext.authenticated === true;
  const isKnownUnauthenticated =
    data?.sessionContext.authenticated === false && !sessionLoading && !sessionError;
  // A launch link (deploycompare.com/?source=devto) carries a CAMPAIGN label.
  // It is kept separate from `source`, which stays the funnel entry stage
  // ("homepage" here) that the demo→signup and homepage→signup funnels read.
  const location = useLocation();
  const campaign = normalizeCampaign(new URLSearchParams(location.search).get("source"));
  const withCampaign = (path: string, params: Record<string, string> = {}) =>
    `${path}?${new URLSearchParams({ ...params, ...(campaign ? { source: campaign } : {}) }).toString()}`;
  const loginHref = (provider?: "gitlab" | "github") =>
    `/login?${new URLSearchParams({
      ...(provider ? { provider } : {}),
      returnTo: "/app",
      source: "homepage",
      ...(campaign ? { campaign } : {}),
    }).toString()}`;
  // The demo is the plan's spearhead, so the label has to survive this hop too:
  // /demo re-parks it for whatever the visitor does next.
  const demoHref = campaign ? withCampaign("/demo") : "/demo";

  const { loadingProvider, startProviderSignIn } = useProviderSignIn({
    started: (provider) => {
      // The known-unauthenticated path starts OAuth from HERE and never visits
      // /login, so this is the only place that can park the label for it.
      saveAuthCampaign(campaign);
      captureEvent("sign_in_started", { provider, source: "homepage", campaign });
    },
    failed: (provider) =>
      captureEvent("sign_in_failed", { provider, source: "homepage", campaign }),
  });
  const navCta = isAuthenticated
    ? { label: "Open app", to: "/app", event: "open_app" }
    : {
        label: "Sign in",
        to: loginHref(),
        event: "sign_in",
      };

  useEffect(() => {
    captureEvent("landing_page_viewed");
  }, []);

  return (
    <div className="home-hero-backdrop flex min-h-screen flex-col" style={marketingThemeStyle}>
      <a
        href="#main-content"
        className="sr-only z-50 rounded-md bg-primary px-4 py-2 font-semibold text-primary-foreground focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to main content
      </a>
      <nav className="sticky top-0 z-40 border-b border-border bg-background/75 px-4 py-3 backdrop-blur-md sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <Link
            to="/"
            className="inline-flex min-h-11 items-center gap-2 text-sm font-bold tracking-tight"
          >
            <LogoMark />
            DeployCompare
          </Link>
          <div className="ml-6 hidden items-center gap-1 md:flex">
            <a
              href="#how-it-works"
              className="inline-flex h-8 items-center rounded-md px-2.5 text-sm text-muted-foreground hover:bg-surface-2/70 hover:text-foreground"
            >
              How it works
            </a>
            <Link
              to="/pricing"
              className="inline-flex h-8 items-center rounded-md px-2.5 text-sm text-muted-foreground hover:bg-surface-2/70 hover:text-foreground"
            >
              Pricing
            </Link>
            <Link
              to="/security"
              className="inline-flex h-8 items-center rounded-md px-2.5 text-sm text-muted-foreground hover:bg-surface-2/70 hover:text-foreground"
            >
              Security
            </Link>
            <Link
              to="/help"
              className="inline-flex h-8 items-center rounded-md px-2.5 text-sm text-muted-foreground hover:bg-surface-2/70 hover:text-foreground"
            >
              Docs
            </Link>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Full navigation (not SPA) so entering the app boots PostHog in
                identified mode — see lib/analytics.ts isAppSurface(). */}
            <a
              href={navCta.to}
              className="inline-flex h-11 items-center rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface-2/70 hover:text-foreground"
              onClick={() =>
                captureEvent("landing_cta_clicked", {
                  cta: navCta.event,
                  location: "header",
                })
              }
            >
              {navCta.label}
            </a>
            <Button asChild size="sm" className="hidden sm:inline-flex">
              <Link
                to={demoHref}
                onClick={() =>
                  captureEvent("landing_cta_clicked", {
                    cta: "view_demo",
                    location: "header",
                  })
                }
              >
                Live demo
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
            <button
              type="button"
              className="grid h-11 w-11 place-items-center rounded-md border border-border text-foreground transition-colors hover:bg-surface-2 md:hidden"
              aria-expanded={mobileNavOpen}
              aria-controls="homepage-mobile-nav"
              aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {mobileNavOpen ? (
          <div
            id="homepage-mobile-nav"
            className="mx-auto grid max-w-6xl gap-1 border-t border-border/70 pt-3 mt-3 md:hidden"
          >
            <MobileNavAnchor href="#how-it-works" onClick={() => setMobileNavOpen(false)}>
              How it works
            </MobileNavAnchor>
            <MobileNavLink to="/pricing" onClick={() => setMobileNavOpen(false)}>
              Pricing
            </MobileNavLink>
            <MobileNavLink to="/security" onClick={() => setMobileNavOpen(false)}>
              Security
            </MobileNavLink>
            <MobileNavLink to="/help" onClick={() => setMobileNavOpen(false)}>
              Docs
            </MobileNavLink>
            <Link
              to={demoHref}
              className="mt-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground"
              onClick={() => {
                setMobileNavOpen(false);
                captureEvent("landing_cta_clicked", {
                  cta: "view_demo",
                  location: "mobile_nav",
                });
              }}
            >
              Try the live demo
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : null}
      </nav>

      <main
        id="main-content"
        className="mx-auto grid w-full max-w-6xl flex-1 gap-16 px-4 py-14 sm:px-6 sm:py-20 lg:gap-24 lg:py-24"
      >
        <section className="mx-auto grid max-w-5xl justify-items-center gap-6 text-center">
          <span className="mt-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 font-mono text-sm lowercase tracking-[0.08em] text-primary sm:mt-5">
            <span className="home-badge-dot" />
            terraform · gitlab ci · github actions
          </span>
          <h1 className="max-w-5xl text-balance text-4xl font-bold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-[76px]">
            Is this Terraform deploy normal, or do you need to{" "}
            <span className="font-medium italic text-primary">investigate?</span>
          </h1>
          <p className="max-w-3xl text-pretty text-lg leading-8 text-muted-foreground">
            DeployCompare tells you in 60 seconds. It compares a candidate pipeline against your
            last successful runs.
          </p>

          <div className="mt-4 grid justify-items-center gap-4">
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="h-12 px-6 text-base">
                <Link
                  to={demoHref}
                  onClick={() =>
                    captureEvent("landing_cta_clicked", {
                      cta: "view_demo",
                      location: "hero",
                    })
                  }
                >
                  Try the live demo
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base">
                <a href="#how-it-works">See how DeployCompare decides</a>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              No login required. Sample project, real Terraform output.
            </p>
          </div>
        </section>

        <div className="grid gap-10">
          <HomepageHaystack />
          <div id="comparison" className="scroll-mt-24">
            <HomepageDemoPreview demoHref={demoHref} />
          </div>
        </div>

        <section id="how-it-works" className="scroll-mt-24 border-y border-border py-12 lg:py-16">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div className="lg:sticky lg:top-28 lg:self-start">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
                From plan to decision
              </p>
              <h2 className="mt-4 max-w-xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                A baseline turns a Terraform diff into a decision.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground">
                A raw plan tells you what changed. DeployCompare adds the history and CI context
                that tell you whether the change deserves your attention.
              </p>
              <div className="mt-8 border-l-2 border-primary/60 pl-4">
                <p className="text-sm font-semibold text-foreground">Zero workflow change</p>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  Keep your current CI runners and plan jobs. DeployCompare reads the result after
                  each run; it never runs or gates an apply.
                </p>
              </div>
            </div>

            <ol className="border-t border-border">
              <DecisionStep
                number="01"
                title="Establish what normal looks like"
                body="Recent successful runs of the same job form the baseline. Normal is what this stack actually does, not a generic policy."
              />
              <DecisionStep
                number="02"
                title="Spot the break from the pattern"
                body={
                  <>
                    DeployCompare compares the candidate with that baseline, so an unusual
                    replacement or unexpected{" "}
                    <code className="font-mono text-foreground">destroy</code> rises above the
                    noise.
                  </>
                }
              />
              <DecisionStep
                number="03"
                title="Follow the evidence"
                body="Every finding links to the exact CI job and the log lines behind it, so you can check the claim yourself."
              />
            </ol>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-primary/25 bg-surface-1/80">
          <div className="grid gap-10 p-6 sm:p-10 lg:grid-cols-[1fr_0.9fr] lg:gap-16 lg:p-12">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
                Compare your own deploys
              </p>
              <h2 className="mt-4 max-w-xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                Seen it work? Connect your repository.
              </h2>
              <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground">
                DeployCompare reads pipeline and job data from GitLab or GitHub. It never writes to
                your repositories or runs Terraform applies.
              </p>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <ProviderButton
                  to={loginHref("gitlab")}
                  provider="gitlab"
                  direct={isKnownUnauthenticated}
                  location="connect"
                  loading={loadingProvider === "gitlab"}
                  disabled={loadingProvider !== null}
                  onClick={() => {
                    captureEvent("landing_cta_clicked", {
                      cta: "gitlab_sign_in",
                      location: "connect",
                    });
                    void startProviderSignIn("gitlab");
                  }}
                />
                <ProviderButton
                  to={loginHref("github")}
                  provider="github"
                  direct={isKnownUnauthenticated}
                  location="connect"
                  loading={loadingProvider === "github"}
                  disabled={loadingProvider !== null}
                  onClick={() => {
                    captureEvent("landing_cta_clicked", {
                      cta: "github_sign_in",
                      location: "connect",
                    });
                    void startProviderSignIn("github");
                  }}
                />
              </div>
            </div>

            <div className="border-t border-border pt-8 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
              <div className="flex items-center gap-3 text-sm font-semibold text-foreground">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                Read-only by design
              </div>
              <ul className="mt-6 divide-y divide-border border-y border-border text-sm text-muted-foreground">
                <li className="py-4">No runner migration or workflow rewrite</li>
                <li className="py-4">No repository writes and no Terraform applies</li>
                <li className="py-1">
                  <Link
                    to="/security"
                    className="inline-flex min-h-11 items-center gap-1.5 font-medium text-foreground hover:text-primary"
                  >
                    Review the security model
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              </ul>
              <details className="group mt-5 text-sm text-muted-foreground">
                <summary className="cursor-pointer py-3 font-medium text-foreground marker:text-primary">
                  Exact access scopes
                </summary>
                <p className="mt-3 leading-6">
                  GitLab uses <code className="font-mono text-foreground">read_api</code> and{" "}
                  <code className="font-mono text-foreground">read_user</code>. GitHub uses a
                  read-only App with Actions, Metadata, and Email addresses permissions.
                </p>
              </details>
            </div>
          </div>
        </section>

        <EmailCaptureSection />
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * Homepage email capture for the marketing list (double opt-in — Mailchimp
 * sends new addresses a confirmation email). Exported for its component test.
 */
export function EmailCaptureSection() {
  const [email, setEmail] = useState("");
  // Honeypot: humans never see this field, so a filled value means a bot —
  // which gets the success state without any request being made.
  const [website, setWebsite] = useState("");
  const [state, setState] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // account-scoped: sends x-operation-kind: account and drops x-connection-id.
  // The auth plugin's public-operation allowlist only admits that kind, and a
  // homepage visitor has no connection — without this the mutation is routed
  // as a connector call and rejected with UNAUTHENTICATED.
  const [subscribe] = useMutation<{ subscribeToUpdates: boolean }>(SUBSCRIBE_TO_UPDATES, {
    context: { operationKind: "account" },
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (state === "submitting") return;
    if (website.trim()) {
      setState("done");
      return;
    }
    setState("submitting");
    setErrorMessage(null);
    captureEvent("email_capture_submitted", { location: "landing" });
    try {
      const result = await subscribe({ variables: { email: email.trim() } });
      if (result.data?.subscribeToUpdates) {
        captureEvent("email_capture_succeeded", { location: "landing" });
        setState("done");
      } else {
        setState("error");
        setErrorMessage("Something went wrong on our side. Please try again in a minute.");
      }
    } catch (err) {
      setState("error");
      setErrorMessage(
        err instanceof Error && /deliverable/i.test(err.message)
          ? err.message
          : "Something went wrong on our side. Please try again in a minute."
      );
    }
  };

  return (
    <section aria-labelledby="beta-notes-heading" className="mx-auto w-full max-w-3xl text-center">
      <p className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
        Not ready to connect yet?
      </p>
      <h2
        id="beta-notes-heading"
        className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl"
      >
        Follow the beta instead
      </h2>
      <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-muted-foreground">
        Occasional development notes and the launch email. No spam, unsubscribe anytime.
      </p>
      {state === "done" ? (
        <p role="status" className="mx-auto mt-8 max-w-xl text-base font-medium text-foreground">
          Almost there. Check your inbox and confirm the subscription.
        </p>
      ) : (
        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="relative mx-auto mt-8 flex w-full max-w-md flex-col gap-3 sm:flex-row"
        >
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <label htmlFor="beta-notes-website">Website</label>
            <input
              id="beta-notes-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
          </div>
          <Input
            id="beta-notes-email"
            type="email"
            required
            aria-label="Email address"
            placeholder="you@company.com"
            autoComplete="email"
            className="h-11 flex-1"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button type="submit" className="h-11 px-5" disabled={state === "submitting"}>
            {state === "submitting" ? "Subscribing…" : "Get beta notes"}
          </Button>
        </form>
      )}
      {state === "error" && errorMessage ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {errorMessage}
        </p>
      ) : null}
      <p className="mt-4 text-xs text-muted-foreground">
        One-click unsubscribe in every email. See the{" "}
        <Link to="/privacy" className="font-medium text-foreground hover:text-primary">
          privacy policy
        </Link>
        .
      </p>
    </section>
  );
}

function LogoMark() {
  return <img src="/favicon.svg" alt="" className="h-6 w-6 rounded-md" width={24} height={24} />;
}

function ProviderButton({
  to,
  provider,
  direct,
  location,
  loading,
  disabled,
  onClick,
}: {
  to: string;
  provider: Exclude<ProviderId, "fixtures">;
  direct: boolean;
  location: "connect";
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = provider === "github" ? Github : Gitlab;
  const providerName = provider === "github" ? "GitHub" : "GitLab";
  const label = `Continue with ${providerName}`;
  const className = cn(
    "inline-flex h-14 w-full min-w-0 items-center justify-center gap-3 rounded-lg border border-border bg-background/55 px-5 text-base font-semibold text-foreground transition hover:border-primary/50 hover:bg-surface-2 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
  );
  const content = (
    <>
      {loading ? <Spinner /> : <Icon className="h-5 w-5" aria-hidden="true" />}
      <span aria-live="polite" aria-atomic="true">
        {loading ? `Starting ${providerName} sign-in…` : label}
      </span>
    </>
  );

  if (!direct) {
    return (
      <Link
        to={to}
        onClick={() =>
          captureEvent("landing_cta_clicked", {
            cta: `${provider}_sign_in`,
            location,
          })
        }
        className={className}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-busy={loading || undefined}
      data-testid={`landing-provider-${provider}`}
      className={className}
    >
      {content}
    </button>
  );
}

function DecisionStep({ number, title, body }: { number: string; title: string; body: ReactNode }) {
  return (
    <li className="grid gap-4 border-b border-border py-7 sm:grid-cols-[4rem_1fr] sm:py-9">
      <span className="font-mono text-sm text-primary">{number}</span>
      <div>
        <h3 className="text-xl font-semibold tracking-tight text-foreground">{title}</h3>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">{body}</p>
      </div>
    </li>
  );
}

function MobileNavAnchor({
  href,
  onClick,
  children,
}: {
  href: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="flex min-h-11 items-center rounded-md px-3 text-sm font-medium text-foreground hover:bg-surface-2"
    >
      {children}
    </a>
  );
}

function MobileNavLink({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex min-h-11 items-center rounded-md px-3 text-sm font-medium text-foreground hover:bg-surface-2"
    >
      {children}
    </Link>
  );
}
